import type { DragEndEvent } from '@dnd-kit/core';
import { closestCenter, DndContext, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { ArrowDropUp } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  IconButton,
  Pagination,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { ExpandedState, SortingState } from '@tanstack/react-table';
import { getCoreRowModel, getExpandedRowModel, useReactTable } from '@tanstack/react-table';
import type { ReactNode } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classSet from 'react-classset';
import { useFormContext as useOptionalFormContext } from 'react-hook-form';

import paginationUtils from '@/utils/paginationUtils';

import DropZoneRow from './DropZoneRow';
import ManageColumnsDialog from './ManageColumnsDialog';
import type { ManualOrderConfig } from './manualOrder.utils';
import TableHeaderCell from './TableHeaderCell';
import type { TableToolBarAction } from './TableToolbar';
import TableToolbar from './TableToolbar';
import { useManualOrder } from './useManualOrder';
import VNTableRow from './VNTableRow';

const DEFAULT_PAGINATION = {
  sortBy: 'createdDate',
  descending: true,
  rowsPerPage: 100,
  page: 1,
  totalItems: 0,
  pages: 1,
};

/** Hard UI cap — Elasticsearch max result window is 10k (~100 pages at 100 rows/page). */
const MAX_PAGES = 100;

interface IProps<T = unknown> {
  columns: any[];
  data?: any[]; // New optional prop
  showRowSelection?: boolean;
  paginationChange?: (pagination: any) => void;
  hidePagination?: boolean;
  loading?: boolean;
  loadingRows?: number;
  showActionbar?: boolean;
  handleActionClick?: (key: string) => void;
  toolBarActions?: TableToolBarAction[];
  toolBarbulkActions?: TableToolBarAction[];
  dataKey?: string; // Key for React Hook Form data
  selectedRowId?: string;
  sortable?: (prevItem: any, newItem: any, prevId: string, newId: string) => void;
  rowRefs?: any;
  noDataMessage?: ReactNode;
  columnCacheKey?: string;
  externalDnd?: boolean;
  tableId?: string;
  showDropZone?: boolean;
  dragSourceOnly?: boolean; //  table is drag source only, no drop/sort
  dragHandleOnly?: boolean; // drag reorder only from the handle column
  hideDragHandles?: boolean; // keep sort column visible but hide row drag handles
  selectionMode?: 'single' | 'multi';
  dragAndDropText?: string;
  getRowProps?: (row: any) => React.HTMLAttributes<HTMLTableRowElement>;
  infoContent?: ReactNode;
  /** Bump to collapse expanded rows. Does not collapse when only row children change. */
  collapseSignal?: number;
  /** Keep the current rows visible while `loading` is true. */
  preserveRowsWhileLoading?: boolean;
  manualOrder?: ManualOrderConfig<T>;
  manualOrderItems?: T[];
  onManualOrderChange?: (items: T[]) => void;
}

const VNTable = <T,>({
  columns,
  data: externalData, // New prop
  showRowSelection = false,
  paginationChange,
  hidePagination = false,
  loading = false,
  loadingRows = 20,
  showActionbar = false,
  handleActionClick,
  toolBarActions = [],
  toolBarbulkActions = [],
  dataKey = 'data',
  selectedRowId = '',
  sortable,
  rowRefs = [],
  noDataMessage = false,
  columnCacheKey,
  externalDnd = false,
  tableId = 'default-table',
  showDropZone = false,
  dragSourceOnly = false,
  dragHandleOnly = false,
  hideDragHandles = false,
  selectionMode = 'multi',
  dragAndDropText = 'Drag and Drop here',
  getRowProps,
  infoContent,
  collapseSignal,
  preserveRowsWhileLoading = false,
  manualOrder,
  manualOrderItems,
  onManualOrderChange,
}: IProps<T>) => {
  const methods = useOptionalFormContext(); // Get form context if available
  const { watch, setValue } = methods || {};
  const watchedSelectedRows = watch ? watch('selectedRows') : [];
  const [selectAll, setSelectAll] = useState(false);
  const [selectAllIndeterminate, setSelectAllIndeterminate] = useState(false);
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [expanded, setExpanded] = React.useState<ExpandedState>({});
  const hideContentWhileLoading = loading && !preserveRowsWhileLoading;
  useEffect(() => {
    setExpanded({});
  }, [collapseSignal]);
  const data = externalData ?? (watch?.(dataKey) || []);
  const paginationDetail = watch?.('pagination') || DEFAULT_PAGINATION;

  const safePaginationDetail = useMemo(() => paginationDetail || DEFAULT_PAGINATION, [paginationDetail]);

  const [pagination, setPagination] = useState({
    pageIndex: safePaginationDetail.page,
    pageSize: safePaginationDetail.rowsPerPage,
  });

  const paginationSummary = useMemo(
    () => paginationUtils.getPaginationSummary(safePaginationDetail, safePaginationDetail.totalItems),
    [safePaginationDetail]
  );

  const pageCount = Math.min(Math.max(safePaginationDetail.pages || 1, 1), MAX_PAGES);
  const currentPage = Math.min(Math.max(safePaginationDetail.page || 1, 1), pageCount);

  const [sorting, setSorting] = useState<SortingState>(() => {
    const found = columns.find((c) => c.accessorKey === safePaginationDetail.sortBy);
    return found
      ? [
          {
            id: safePaginationDetail.sortBy,
            desc: safePaginationDetail.descending,
          },
        ]
      : [];
  });

  // --- NEW: COLUMN CACHE AND VISIBILITY LOGIC ---
  const [openManageDialog, setOpenManageDialog] = useState(false);

  const [visibleColumns, setVisibleColumns] = useState(() => {
    // If no cache key is provided, show all columns by default
    if (!columnCacheKey) return columns.map((col) => ({ ...col, visible: true }));

    const defaultPrefs = columns.map((col) => ({
      ...col,
      id: col.id || col.accessorKey,
      visible: true,
      locked: col.meta?.locked || false,
    }));

    try {
      const storedPrefs = sessionStorage.getItem(columnCacheKey);
      if (storedPrefs) {
        const parsedPrefs = JSON.parse(storedPrefs);
        return defaultPrefs.map((col) => {
          const storedCol = parsedPrefs.find((p: any) => p.id === (col.id || col.accessorKey));
          if (storedCol) {
            return { ...col, visible: storedCol.visible, locked: storedCol.locked };
          }
          return col;
        });
      }
    } catch (e) {
      console.error(`Failed to parse column preferences for ${columnCacheKey}`, e);
    }
    return defaultPrefs;
  });

  // Sync to session storage when columns change
  useEffect(() => {
    if (!columnCacheKey) return;
    const stateToSave = visibleColumns.map((col) => ({
      id: col.id || col.accessorKey,
      visible: col.visible,
      locked: col.locked ?? col.meta?.locked ?? false,
    }));
    sessionStorage.setItem(columnCacheKey, JSON.stringify(stateToSave));
  }, [visibleColumns, columnCacheKey]);

  // Memoize the filtered columns to pass to TanStack Table
  const filteredColumns = useMemo(() => {
    if (!columnCacheKey) return columns;
    return columns.filter(
      (col) => visibleColumns.find((vc) => (vc.id || vc.accessorKey) === (col.id || col.accessorKey))?.visible
    );
  }, [columns, visibleColumns, columnCacheKey]);

  const table = useReactTable({
    data,
    columns: filteredColumns,
    state: { sorting, pagination, expanded },
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange: setPagination,
    onExpandedChange: setExpanded,
    getSubRows: (row) => row.children,
    getExpandedRowModel: getExpandedRowModel(),
    defaultColumn: {
      size: 'auto',
    },
    meta: {
      openManageColumns: () => setOpenManageDialog(true),
    },
  });
  const { rows } = table.getRowModel();

  useEffect(() => {
    // Only clear if data is empty AND there's currently something selected
    if ((!data || data.length === 0) && watchedSelectedRows?.length > 0) {
      setValue?.('selectedRows', []);
    }
  }, [data, setValue, watchedSelectedRows?.length]);

  useEffect(() => {
    const hasRows = data?.length > 0;
    const hasSelection = watchedSelectedRows?.length > 0;

    if (hasRows && hasSelection) {
      const isAllSelected = watchedSelectedRows.length === data.length;
      // Only update if the boolean state actually differs
      setSelectAll((prev) => (prev !== isAllSelected ? isAllSelected : prev));
      setSelectAllIndeterminate((prev) => (prev !== !isAllSelected ? !isAllSelected : prev));
    } else {
      setSelectAll((prev) => (prev !== false ? false : prev));
      setSelectAllIndeterminate((prev) => (prev !== false ? false : prev));
    }
  }, [watchedSelectedRows, data?.length]); // Added data.length to dependencies

  const handlePageChange = useCallback(
    (_: unknown, value: number) => {
      if (value > MAX_PAGES) {
        return;
      }
      const nextPage = Math.min(Math.max(value, 1), MAX_PAGES);
      setPagination((prev) => ({ ...prev, pageIndex: nextPage }));
      paginationChange?.({ ...safePaginationDetail, page: nextPage });
      if (setValue) {
        setValue('selectedRows', []);
      }
    },
    [safePaginationDetail, paginationChange]
  );

  const sensors = useSensors(
    useSensor(MouseSensor, {
      // Require the mouse to move 8px before dragging starts
      // This allows normal "clicks" to fire on child elements
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      // For touch devices, a delay is usually better to allow scrolling
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {})
  );
  const handleRequestSort = useCallback(
    (column: any) => {
      if (!column.columnDef?.meta?.sortable) return;

      const isDesc = safePaginationDetail.sortBy === column.id ? !safePaginationDetail.descending : false;
      setSorting([{ id: column.id, desc: isDesc }]);
      setPagination((prev) => ({ ...prev, pageIndex: 1 }));
      paginationChange?.({
        ...safePaginationDetail,
        sortBy: column.id,
        descending: isDesc,
        page: 1,
      });
    },
    [safePaginationDetail, paginationChange]
  );

  const handleCustomOrderSort = useCallback(() => {
    if (!sortable && !manualOrder) {
      return;
    }

    setSorting([]);
    setPagination((prev) => ({ ...prev, pageIndex: 1 }));
    paginationChange?.({
      ...safePaginationDetail,
      sortBy: 'customOrder',
      descending: false,
      page: 1,
    });
  }, [manualOrder, paginationChange, safePaginationDetail, sortable]);

  const ensureCustomOrderSort = useCallback(() => {
    if (safePaginationDetail.sortBy === 'customOrder') {
      return;
    }

    setSorting([]);
    paginationChange?.({
      ...safePaginationDetail,
      sortBy: 'customOrder',
      descending: false,
    });
  }, [paginationChange, safePaginationDetail]);

  const { handleDragReorder, handleMoveAction, getMenuState } = useManualOrder<T>({
    config: manualOrder,
    items: manualOrderItems ?? [],
    onChange: onManualOrderChange,
    onEnsureCustomOrder: ensureCustomOrderSort,
  });

  const handleSelectAll = useCallback(() => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    const allRows = table.getRowModel().rows;
    const newSelection = newSelectAll ? Object.fromEntries(allRows.map((row) => [row.id, true])) : {};
    setRowSelection(newSelection);
    if (setValue) {
      const selectedOriginalRows = newSelectAll ? allRows.map((row) => row.original) : [];
      if (setValue) {
        setValue('selectedRows', selectedOriginalRows);
      }
    }
  }, [selectAll]);

  const handleRowSelect = useCallback(
    (rowId: string) => {
      setRowSelection((prev) => {
        const isCurrentlySelected = Boolean(prev[rowId]);
        const updatedRowSelection =
          selectionMode === 'single'
            ? isCurrentlySelected
              ? {}
              : { [rowId]: true } // toggle single row
            : { ...prev, [rowId]: !prev[rowId] }; // multi: toggle in place
        const selectedOriginalRows = table
          .getRowModel()
          .rows.filter((row) => updatedRowSelection[row.id])
          .map((row) => row.original);
        if (setValue) {
          setValue('selectedRows', selectedOriginalRows);
        }
        return updatedRowSelection;
      });
    },
    [setRowSelection]
  );

  const handleDragEnd = (event: DragEndEvent) => {
    if (!event.active?.id || !event.over?.id) {
      return;
    }

    const activeId = String(event.active.id);
    const overId = String(event.over.id);

    if (manualOrder) {
      handleDragReorder(activeId, overId);
      return;
    }

    const allRows = table.getRowModel().rows;
    sortable?.(
      allRows.find((r) => (r.original.id ?? r.id) === event.active.id)?.original,
      allRows.find((r) => (r.original.id ?? r.id) === event.over.id)?.original,
      activeId,
      overId
    );
  };

  const toggleColumnVisibility = useCallback((columnId: string) => {
    setVisibleColumns((prev) =>
      prev.map((col) => ((col.id || col.accessorKey) === columnId ? { ...col, visible: !col.visible } : col))
    );
  }, []);

  const onActionClick = useCallback(
    async (key: string) => {
      if (key === 'density') {
        const hasThumbnailSrc = columns.some((col) => col.accessorKey === 'thumbnailSrc');
        const columnKey = hasThumbnailSrc ? 'thumbnailSrc' : 'tmp.thumbnailSrc';
        toggleColumnVisibility(columnKey);
        return;
      }
      await handleActionClick?.(key);
      setRowSelection({});
    },
    [handleActionClick]
  );

  const isManualOrderMode = Boolean(manualOrder);
  const manualOrderHandlesHidden = isManualOrderMode && safePaginationDetail.sortBy !== 'customOrder';
  const resolvedHideDragHandles = isManualOrderMode ? manualOrderHandlesHidden : hideDragHandles;
  const showSortColumn = Boolean(sortable) || isManualOrderMode || externalDnd;
  const enableRowDrag =
    (Boolean(sortable) && !resolvedHideDragHandles) ||
    (isManualOrderMode && !resolvedHideDragHandles) ||
    externalDnd ||
    dragSourceOnly;
  const showManualOrderHeader = Boolean(sortable) || isManualOrderMode;

  const tableContent = (
    <TableContainer component={Paper} className="table-container">
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            {showSortColumn && (
              <TableCell
                className={classSet({
                  'header-cell': true,
                  'sortable-cell': true,
                  'sortable-cell--manual-order': isManualOrderMode,
                })}
                sx={{ textAlign: 'center' }}
              >
                {showManualOrderHeader ? (
                  <IconButton className="sort-btn sort-btn-manual" onClick={handleCustomOrderSort} title="Sort by manual order">
                    <ArrowDropUp
                      className={classSet({
                        active: safePaginationDetail.sortBy === 'customOrder',
                      })}
                    />
                  </IconButton>
                ) : null}
              </TableCell>
            )}
            {showRowSelection && (
              <TableCell className="header-cell checkbox-cell">
                {selectionMode === 'multi' && (
                  <Checkbox checked={selectAll} indeterminate={selectAllIndeterminate} onChange={handleSelectAll} size="small" />
                )}
              </TableCell>
            )}
            {table
              .getHeaderGroups()
              .map((headerGroup) =>
                headerGroup.headers.map((header) => (
                  <TableHeaderCell
                    key={header.id}
                    header={header}
                    paginationDetail={safePaginationDetail}
                    onRequestSort={handleRequestSort}
                  />
                ))
              )}
          </TableRow>
        </TableHead>
        <TableBody>
          {hideContentWhileLoading ? (
            // Show skeleton loader while loading
            Array.from({ length: loadingRows }).map((_, index) => (
              <TableRow key={index}>
                {showSortColumn && (
                  <TableCell className="sort-cell">
                    {!resolvedHideDragHandles ? <Skeleton variant="rectangular" width={24} height={24} /> : null}
                  </TableCell>
                )}
                {showRowSelection && (
                  <TableCell className="checkbox-cell">
                    <Skeleton variant="rectangular" width={24} height={24} />
                  </TableCell>
                )}
                {filteredColumns.map((column, colIndex) => (
                  <TableCell
                    key={colIndex}
                    sx={{
                      // Lock column widths to match the header so the table doesn't jump
                      // when real data replaces the skeleton placeholders.
                      width: column.size || 'auto',
                      minWidth: column.size || 'auto',
                      maxWidth: column.size || 'auto',
                    }}
                  >
                    <Skeleton variant="text" width="80%" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <>
              {rows.length > 0 && (
                <SortableContext
                  id={tableId}
                  items={rows.map((i) => (externalDnd ? `${tableId}-${i.original.id ?? i.id}` : i.original.id ?? i.id))}
                  strategy={
                    dragSourceOnly
                      ? () => null // No layout strategy — disables drop targeting
                      : enableRowDrag
                      ? verticalListSortingStrategy
                      : () => null
                  }
                  disabled={!enableRowDrag}
                >
                  {rows.map((row) => {
                    return (
                      <VNTableRow
                        key={row.id}
                        row={row}
                        selectedRowId={selectedRowId}
                        showRowSelection={showRowSelection}
                        rowSelection={rowSelection}
                        handleRowSelect={handleRowSelect}
                        sortable={showSortColumn}
                        enableRowDrag={enableRowDrag}
                        dragHandleOnly={dragHandleOnly}
                        hideDragHandles={resolvedHideDragHandles}
                        manualOrderEnabled={isManualOrderMode && enableRowDrag}
                        manualOrderMenuState={getMenuState(String(row.original.id ?? row.id))}
                        onManualOrderMove={handleMoveAction}
                        rowRefs={rowRefs}
                        tableId={tableId}
                        externalDND={externalDnd}
                        {...(getRowProps ? getRowProps(row) : {})}
                      />
                    );
                  })}
                </SortableContext>
              )}
              {/* ── Drop Zone Row ─────────────────────────────────────── */}
              {showDropZone && externalDnd && (
                <DropZoneRow
                  tableId={tableId}
                  dragAndDropText={dragAndDropText}
                  colSpan={
                    table.getVisibleFlatColumns().length +
                    (showSortColumn ? 1 : 0) + // drag handle col
                    (showRowSelection ? 1 : 0) // checkbox col
                  }
                />
              )}

              {rows.length === 0 && !showDropZone && (
                <TableRow>
                  <TableCell colSpan="100%" className="empty-cell">
                    {noDataMessage || <Typography sx={{ textAlign: 'center' }}>No data available</Typography>}
                  </TableCell>
                </TableRow>
              )}
            </>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <div className="vn-table">
      {showActionbar && <TableToolbar onActionClick={onActionClick} actions={toolBarActions} bulkActions={toolBarbulkActions} />}
      {infoContent ? <div className="vn-table__info">{infoContent}</div> : null}
      <div
        className={classSet({
          'table-wrapper': true,
          'with-pagination': !hidePagination,
          'with-header': showActionbar,
          'with-info': !!infoContent,
        })}
      >
        {externalDnd ? (
          tableContent
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={handleDragEnd}
          >
            {tableContent}
          </DndContext>
        )}
      </div>
      {!hidePagination && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt={2}
          mb={2}
          flexDirection={{ xs: 'column', sm: 'row' }}
        >
          <Typography>
            Showing {paginationSummary.from} - {paginationSummary.to} of {paginationSummary.total}
          </Typography>
          {!hideContentWhileLoading && (
            <Pagination
              size="small"
              count={pageCount}
              page={currentPage}
              variant="outlined"
              shape="rounded"
              color="primary"
              onChange={handlePageChange}
              siblingCount={0}
              boundaryCount={1}
            />
          )}
        </Box>
      )}
      {openManageDialog && (
        <ManageColumnsDialog
          open={openManageDialog}
          onClose={() => setOpenManageDialog(false)}
          visibleColumns={visibleColumns}
          setVisibleColumns={setVisibleColumns}
        />
      )}
    </div>
  );
};

export default React.memo(VNTable) as <T = unknown>(props: IProps<T>) => React.ReactElement | null;
